import { GetStaticPaths, GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import { NextSeo } from 'next-seo'
import { ReactElement } from 'react'

import type {
  CampaignPublic,
  CampaignRegion,
  WorldMapShapes
} from '@core/journeys/ui/Campaign'
import { matchRegionSlug } from '@core/journeys/ui/Campaign/libs/matchRegionSlug'
import { CAMPAIGN_LANGUAGE_COOKIE } from '@core/journeys/ui/Campaign/libs/resolvePageLanguage'
import { getJourneyRTL } from '@core/journeys/ui/rtl'
import { GET_JOURNEY } from '@core/journeys/ui/useJourneyQuery'

import {
  GetJourney,
  GetJourneyVariables,
  GetJourney_journey as Journey
} from '../../__generated__/GetJourney'
import { CampaignPageKind, IdType } from '../../__generated__/globalTypes'
import i18nConfig from '../../next-i18next.config'
import { JourneyPageWrapper } from '../../src/components/JourneyPageWrapper'
import { JourneyRenderer } from '../../src/components/JourneyRenderer'
import { createApolloClient } from '../../src/libs/apolloClient'
import { getHostCampaignStaticProps } from '../../src/libs/campaignStaticProps'
import { getFlags } from '../../src/libs/getFlags'
import { isJourneyNotFoundError } from '../../src/libs/isJourneyNotFoundError'
import { JOURNEY_STATUS_EXCLUDE_DRAFT } from '../../src/libs/journeyQueryOptions'

// The campaign branch only renders for a Campaign Domain; journey visitors
// never download it.
const CampaignPageWrapper = dynamic(
  async () =>
    (
      await import(
        /* webpackChunkName: "CampaignPageWrapper" */ '../../src/components/CampaignPageWrapper'
      )
    ).CampaignPageWrapper
)

interface HostJourneyPageProps {
  host: string
  journey: Journey
  locale: string
  rtl: boolean
}

export function HostJourneyPage({
  host,
  journey,
  locale,
  rtl
}: HostJourneyPageProps): ReactElement {
  const router = useRouter()
  const isIframe = typeof window !== 'undefined' && window.self !== window.top
  if (isIframe) {
    void router.push('/embed/[journeySlug]', `/embed/${journey.slug}`)
  }

  return (
    <>
      <Head>
        <link
          rel="alternate"
          type="application/json+oembed"
          href={`https://${
            process.env.NEXT_PUBLIC_VERCEL_URL ?? 'your.nextstep.is'
          }/api/oembed?url=https%3A%2F%2F${
            process.env.NEXT_PUBLIC_VERCEL_URL ?? 'your.nextstep.is'
          }%2F${journey.slug}&format=json`}
          title={journey.seoTitle ?? undefined}
        />
      </Head>
      <NextSeo
        nofollow
        noindex
        title={journey.seoTitle ?? undefined}
        description={journey.seoDescription ?? undefined}
        openGraph={{
          type: 'website',
          title: journey.seoTitle ?? undefined,
          url: `https://${host}/${journey.slug}`,
          description: journey.seoDescription ?? undefined,
          images:
            journey.primaryImageBlock?.src != null
              ? [
                  {
                    url: journey.primaryImageBlock.src,
                    width: journey.primaryImageBlock.width,
                    height: journey.primaryImageBlock.height,
                    alt: journey.primaryImageBlock.alt,
                    type: 'image/jpeg'
                  }
                ]
              : []
        }}
      />
      <JourneyPageWrapper journey={journey} rtl={rtl} locale={locale}>
        <JourneyRenderer />
      </JourneyPageWrapper>
    </>
  )
}

interface HostSlugPageProps {
  host?: string
  journey?: Journey
  locale?: string
  rtl?: boolean
  campaign?: CampaignPublic
  region?: CampaignRegion | null
  worldMap?: WorldMapShapes | null
}

// `/<segment>` on a domain is a region of its campaign first, then a
// journey slug (a region outranks a journey with the same spelling).
function HostSlugPage({
  campaign,
  region,
  worldMap,
  journey,
  host,
  locale,
  rtl
}: HostSlugPageProps): ReactElement {
  if (campaign != null && region != null)
    return (
      <CampaignPageWrapper
        campaign={campaign}
        pageKind={CampaignPageKind.regionTemplate}
        region={region}
        worldMap={worldMap}
        basePath=""
      />
    )

  if (journey == null) return <></>

  return (
    <HostJourneyPage
      host={host ?? ''}
      journey={journey}
      locale={locale ?? 'en'}
      rtl={rtl ?? false}
    />
  )
}

export const getStaticProps: GetStaticProps<HostSlugPageProps> = async (
  context
) => {
  const apolloClient = createApolloClient()
  const campaignProps = await getHostCampaignStaticProps({
    hostname: context.params?.hostname?.toString() ?? '',
    segment: context.params?.journeySlug?.toString(),
    locale: context.locale
  })
  if (campaignProps != null) return campaignProps

  try {
    const { data } = await apolloClient.query<GetJourney, GetJourneyVariables>({
      query: GET_JOURNEY,
      variables: {
        id: context.params?.journeySlug?.toString() ?? '',
        idType: IdType.slug,
        options: {
          hostname: context.params?.hostname?.toString() ?? '',
          status: JOURNEY_STATUS_EXCLUDE_DRAFT
        }
      }
    })
    // Apollo Client 4 types `data` as optional. The default `none` error
    // policy rejects rather than resolving without data, so this narrows
    // the type rather than guarding a reachable branch.
    if (data == null) throw new Error('GetJourney returned no data')

    const { rtl, locale } = getJourneyRTL(data.journey)
    return {
      props: {
        flags: await getFlags(),
        hostname: context.params?.hostname?.toString() ?? '',
        host: context.params?.host?.toString() ?? '',
        ...(await serverSideTranslations(
          locale ?? 'en',
          ['apps-journeys', 'libs-journeys-ui'],
          i18nConfig
        )),
        journey: data.journey,
        locale,
        rtl
      },
      revalidate: 60
    }
  } catch (e) {
    if (isJourneyNotFoundError(e)) {
      return {
        props: {
          ...(await serverSideTranslations(
            context.locale ?? 'en',
            ['apps-journeys', 'libs-journeys-ui'],
            i18nConfig
          ))
        },
        notFound: true,
        revalidate: 1
      }
    }
    throw e
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export default HostSlugPage
